"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react"
import { useLocation } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { managedEmployerOrgSlugStorageKey, managedEmployerOrganisationSwitcherEnabled, EMPLOYER_HOME_PATH } from "@/constants"
import { useAuth } from "@/hooks/use-auth"
import { useFocusStore } from "@/hooks/use-focus-store"
import { useOrganisations, type Organisation } from "@/hooks/use-organisations"

/** Slug embedded in `/employer/organisation/:slug` or `/employer/organisations/:slug/…`. */
export function employerPathManagedOrgSlug(pathname: string): string | null {
  const parts = pathname.replace(/\/+$/, "").split("/").filter(Boolean)
  if (parts.length < 3 || parts[0] !== "employer") return null
  if (parts[1] === "organisation" && parts[2]) return decodeURIComponent(parts[2])
  if (parts[1] === "organisations" && parts[2]) return decodeURIComponent(parts[2])
  return null
}

export { organisationSwitcherAcronym } from "@/utils/organisation-switcher-utils"

import { invalidateEmployerOrganisationScopedQueries } from '@/utils/employer-query-invalidation'

export { invalidateEmployerOrganisationScopedQueries }

export type EmployerOrganisationContextValue = {
  /** True while authenticated employer or interviewer focus — managed-org list fetched in this provider. */
  enabled: boolean
  organisations: Organisation[]
  organisationsLoading: boolean
  activeOrganisationSlug: string | null
  activeOrganisation: Organisation | null
  setActiveOrganisationSlug: (slug: string, options?: { deferInvalidate?: boolean }) => void
}

const EmployerOrganisationContext = createContext<EmployerOrganisationContextValue | null>(null)

function useReconciledActiveSlug(opts: {
  pathname: string
  organisations: Organisation[]
  organisationsLoading: boolean
  queryEnabled: boolean
  storageKey: string
}): [string | null, Dispatch<SetStateAction<string | null>>] {
  const { pathname, organisations, organisationsLoading, queryEnabled, storageKey } = opts
  const [activeOrganisationSlug, setActiveSlug] = useState<string | null>(null)

  useEffect(() => {
    if (!queryEnabled) return
    if (organisationsLoading) return

    const allowed = new Set(organisations.map((o) => o.slug))
    const pathSlug = employerPathManagedOrgSlug(pathname)
    let stored: string | null = null
    try {
      stored = window.localStorage.getItem(storageKey)
    } catch {
      /* ignore */
    }

    let next: string | null = null
    if (pathSlug && allowed.has(pathSlug)) next = pathSlug
    else if (stored && allowed.has(stored)) next = stored
    else if (organisations[0]?.slug) next = organisations[0].slug

    setActiveSlug((prev) => (prev === next ? prev : next))
  }, [pathname, organisations, organisationsLoading, queryEnabled, storageKey])

  useEffect(() => {
    if (!queryEnabled) return
    if (organisationsLoading) return
    try {
      if (activeOrganisationSlug) {
        window.localStorage.setItem(storageKey, activeOrganisationSlug)
      } else {
        window.localStorage.removeItem(storageKey)
      }
    } catch {
      /* ignore */
    }
  }, [activeOrganisationSlug, organisationsLoading, queryEnabled, storageKey])

  return [activeOrganisationSlug, setActiveSlug]
}

function EmployerOrganisationProviderInner({ children }: { children: ReactNode }) {
  const { token, isAuthenticated } = useAuth()
  const { focus } = useFocusStore()
  const managedOrgFocus =
    focus?.current_focus === "employer" || focus?.current_focus === "interviewer"
  const { pathname } = useLocation()
  const queryEnabled = Boolean(token && isAuthenticated && managedOrgFocus)

  const { organisations, isLoading: organisationsLoading } = useOrganisations({
    managedListEnabled: queryEnabled,
  })

  const queryClient = useQueryClient()
  const storageKey = managedEmployerOrgSlugStorageKey()
  const pendingInvalidateRef = useRef(false)

  const [activeOrganisationSlug, setActiveSlug] = useReconciledActiveSlug({
    pathname,
    organisations,
    organisationsLoading,
    queryEnabled,
    storageKey,
  })

  useEffect(() => {
    if (!pendingInvalidateRef.current) return
    if (pathname !== EMPLOYER_HOME_PATH) return
    pendingInvalidateRef.current = false
    invalidateEmployerOrganisationScopedQueries(queryClient)
  }, [pathname, activeOrganisationSlug, queryClient])

  const setActiveOrganisationSlug = useCallback(
    (slug: string, options?: { deferInvalidate?: boolean }) => {
      const ok = organisations.some((o) => o.slug === slug)
      if (!ok || !queryEnabled) return
      setActiveSlug(slug)
      try {
        window.localStorage.setItem(storageKey, slug)
      } catch {
        /* ignore */
      }
      try {
        const url = new URL(window.location.href)
        if (url.searchParams.has("thread")) {
          url.searchParams.delete("thread")
          const next = `${url.pathname}${url.search}${url.hash}`
          window.history.replaceState(window.history.state, "", next)
        }
      } catch {
        /* ignore */
      }
      if (options?.deferInvalidate) {
        pendingInvalidateRef.current = true
        return
      }
      invalidateEmployerOrganisationScopedQueries(queryClient)
    },
    [organisations, queryClient, queryEnabled, storageKey, setActiveSlug],
  )

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.storageArea !== window.localStorage || e.key !== storageKey) return
      const slug = e.newValue
      if (!slug || !organisations.some((o) => o.slug === slug)) return
      setActiveSlug(slug)
      invalidateEmployerOrganisationScopedQueries(queryClient)
    }

    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [organisations, queryClient, storageKey, setActiveSlug])

  const activeOrganisation = useMemo(() => {
    if (!activeOrganisationSlug) return null
    return organisations.find((o) => o.slug === activeOrganisationSlug) ?? null
  }, [organisations, activeOrganisationSlug])

  const value = useMemo<EmployerOrganisationContextValue>(
    () => ({
      enabled: queryEnabled,
      organisations,
      organisationsLoading,
      activeOrganisationSlug,
      activeOrganisation,
      setActiveOrganisationSlug,
    }),
    [
      queryEnabled,
      organisations,
      organisationsLoading,
      activeOrganisationSlug,
      activeOrganisation,
      setActiveOrganisationSlug,
    ],
  )

  return (
    <EmployerOrganisationContext.Provider value={value}>{children}</EmployerOrganisationContext.Provider>
  )
}

/** TalentCo opted-in employer org context — no-op wrapper when disabled (context consumers get `null`). */
export function EmployerOrganisationProvider({ children }: { children: ReactNode }) {
  if (!managedEmployerOrganisationSwitcherEnabled()) {
    return children
  }
  return <EmployerOrganisationProviderInner>{children}</EmployerOrganisationProviderInner>
}

/** When `EmployerOrganisationProvider` omitted or feature disabled, returns `null`. */
export function useEmployerOrganisationOptional(): EmployerOrganisationContextValue | null {
  return useContext(EmployerOrganisationContext)
}
